import { service } from '@ember/service'
import Component from '@glimmer/component'
import { isHTMLSafe, htmlSafe } from '@ember/template'
import { colors } from '@colors'
import { tracked } from '@glimmer/tracking'
import { t, type IntlService } from 'ember-intl'
import { ChartInfoTooltip } from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import optional from 'teacher-ui-client/helpers/optional'
import gradeName from 'teacher-ui-client/helpers/grade-name'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { type LegendsItem, Legend, TH, TD } from 'teacher-ui-client/components/giraffe/primitives/history-tables'
import type Student from 'teacher-ui-client/models/student'
import type { UIScopeName } from 'teacher-ui-client/reporting/ui-scope/route'
import { DataProcessor } from '@blakeelearning/data-tables/utils/data-processor'
import { differenceWith } from 'lodash'
import type RouterService from '@ember/routing/router-service'
import SortColumnIcon from 'teacher-ui-client/components/sort-column-icon'
import BaseHighcharterComponent from 'teacher-ui-client/components/base/highcharter'
import type Owner from '@ember/owner'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface HistoryDataViewModel {
  id: string
  studentId: string
  firstName: string
  lastName: string
  teacherName: string
  gradePosition: number
  spelling: Nullable<number>
  grammar: Nullable<number>
  vocabulary: Nullable<number>
  usage: Nullable<number>
  eventsCompleted: number
  accuracy: Nullable<number>
}

export type StadiumEventsHistoryDataItem = {
  student_id: number
  spelling: number
  grammar: number
  vocabulary: number
  usage: number
  accuracy: number
}

interface Args {
  data: StadiumEventsHistoryDataItem[]
  subco: boolean
  tooltip: string
  uiScope: UIScopeName
  students: Student[]
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

type CategoryKey = 'spelling' | 'grammar' | 'vocabulary' | 'usage'
const chartCategories: CategoryKey[] = ['usage', 'vocabulary', 'grammar', 'spelling']
const legendCategories: CategoryKey[] = ['spelling', 'grammar', 'vocabulary', 'usage']

interface ChartArgs {
  data: HistoryDataViewModel
  showYAxisLabels: boolean
  maxValue: number
}

const buildChartOptions = function (chartOptions: ChartArgs): Highcharts.Options {
  const yAxis: Highcharts.YAxisOptions = {
    title: {
      text: '',
    },
    opposite: true,
    max: chartOptions.maxValue,
    minorTicks: false,
    labels: {
      enabled: chartOptions.showYAxisLabels,
    },
    offset: -10,
  }

  const series = chartCategories.map((category) => {
    return {
      type: 'bar' as const,
      name: category,
      data: [chartOptions.data[category] ?? 0],
    }
  })

  return {
    series,
    chart: {
      type: 'bar',
      // Make some room for the yaxis labels
      margin: chartOptions.showYAxisLabels ? [20, 0, 0, 0] : [0, 0, 0, 0],
      plotBackgroundColor: colors.dustyBlack50,
      // always disable animation for this chart, as they're all separate charts and don't animate together
      animation: false,
    },
    tooltip: {
      enabled: true,
      formatter: function (this: Highcharts.Point) {
        return `<b>${this.y}</b>`
      },
      backgroundColor: colors.dustyBlack500,
      borderColor: colors.dustyBlack500,
      style: {
        color: colors.white,
      },
    },
    colors: [colors.purplyPink300, colors.juicyOrange300, colors.oceanyBlue400, colors.oceanyBlue300],
    credits: {
      enabled: false,
    },
    title: {
      text: '',
    },
    xAxis: {
      maxPadding: 0,
      minPadding: 0,
      labels: {
        enabled: false,
      },
      lineWidth: 0,
      tickWidth: 0,
      gridLineWidth: 0,
      minorGridLineWidth: 0,
    },
    legend: { enabled: false },
    yAxis,
    plotOptions: {
      bar: {
        animation: false,
        borderWidth: 0,
        colorByPoint: false,
        stacking: 'normal',
        pointWidth: 40,
      },
    },
  }
}

export class StadiumEventsHistory extends Component<Signature> {
  @service intl!: IntlService

  @service router!: RouterService

  @tracked sortingPriorityQueue = []

  processor: DataProcessor<HistoryDataViewModel>

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    this.processor = new DataProcessor(this)
  }

  transitionToStudentRoute = (studentId: string) => {
    void this.router.transitionTo('reporting.ui-scope.rex.student-summary', studentId)
  }

  get data() {
    return this.tableRows
  }

  get tableRows() {
    const stats = this.args.data

    return this.args.students.filter(hasId).map((student) => {
      const statRow = stats.find((stat) => String(stat.student_id) === student.id)

      const spelling = statRow?.spelling ?? null
      const grammar = statRow?.grammar ?? null
      const vocabulary = statRow?.vocabulary ?? null
      const usage = statRow?.usage ?? null
      const eventsCompleted = (spelling ?? 0) + (grammar ?? 0) + (vocabulary ?? 0) + (usage ?? 0)

      return {
        firstName: student.firstName,
        lastName: student.lastName,
        teacherName: student.readingTeacherNames.join(', '),
        gradePosition: student.gradePosition,
        id: student.id,
        studentId: student.id,
        spelling,
        grammar,
        vocabulary,
        usage,
        eventsCompleted,
        accuracy: statRow?.accuracy ?? null,
      }
    })
  }

  /**
   * Will amend tooltip message with a note about discrepancies in student numbers
   * only if a discrepancy exists
   */
  get amendedTooltip() {
    const { tooltip } = this.args
    const missingStudentsCount = differenceWith(
      this.args.students,
      this.args.data ?? [],
      (student, historyStudent) => student.id === String(historyStudent.student_id),
    ).length

    if (missingStudentsCount) {
      const wasSafe = isHTMLSafe(tooltip)
      const newTooltip = this.args.tooltip + this.intl.t('reporting.missingStudentsCount', { missingStudentsCount })
      return wasSafe ? htmlSafe(newTooltip) : newTooltip
    }
    return tooltip
  }

  get maxEvents() {
    const eventsData = this.args.data.map((student) => {
      const spelling = student.spelling ?? 0
      const grammar = student.grammar ?? 0
      const vocabulary = student.vocabulary ?? 0
      const usage = student.usage ?? 0
      return spelling + grammar + vocabulary + usage
    })

    const highestEventsDataEarned = eventsData ? Math.max(...eventsData) : 10
    return Math.ceil(highestEventsDataEarned / 2) * 2 + 2 // extra space on the chart (nearest multiple of 2)
  }

  get rowsWithChartOptions() {
    return this.processor.transformedData.map((row, index) => {
      return {
        ...row,
        chartOptions: buildChartOptions({
          data: row,
          showYAxisLabels: index === 0,
          maxValue: this.maxEvents,
        }),
      }
    })
  }

  get legendCategories(): LegendsItem[] {
    const { intl } = this

    return legendCategories.map((category) => {
      switch (category) {
        case 'spelling':
          return {
            color: 'bg-oceany-blue-300',
            label: intl.t('reporting.charts.stadiumEvents.categories.spelling'),
          }
        case 'grammar':
          return {
            color: 'bg-oceany-blue-400',
            label: intl.t('reporting.charts.stadiumEvents.categories.grammar'),
          }
        case 'vocabulary':
          return {
            color: 'bg-juicy-orange-300',
            label: intl.t('reporting.charts.stadiumEvents.categories.vocabulary'),
          }
        case 'usage':
          return {
            color: 'bg-purply-pink-300',
            label: intl.t('reporting.charts.stadiumEvents.categories.usage'),
          }
        default:
          return { color: '', label: '' }
      }
    })
  }

  get showChart() {
    return this.args.uiScope !== 'school'
  }

  <template>
    <div
      data-test-giraffe-stadium-events-history
      class="border-dusty-black-50 shadow-dusty-black-100 relative rounded-sm border shadow-md"
      ...attributes
    >
      <ChartInfoTooltip data-test-stadium-events-tooltip @tooltipContent={{this.amendedTooltip}} />
      <div class="flex overflow-x-auto print:block">
        <table data-test-history-table-stadium-events class="w-full min-w-6xl overflow-hidden rounded-sm text-sm">
          <thead>
            <tr class="bg-oceany-blue-300 divide-x divide-white font-semibold text-white">
              <TH class="w-36" role="button" {{on "click" (fn this.processor.tableActions.sortByValue "firstName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.firstName" htmlSafe=true}}
                  <SortColumnIcon class @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="firstName" />
                </div>
              </TH>
              <TH class="w-36" role="button" {{on "click" (fn this.processor.tableActions.sortByValue "lastName")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.lastName" htmlSafe=true}}

                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="lastName" />
                </div>
              </TH>
              {{#if @subco}}
                <TH
                  class="w-48"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByValue "teacherName")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "reporting.dashboardTables.teacher"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="teacherName" />
                  </div>
                </TH>
                <TH
                  class="w-24"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByNumber "gradePosition")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "grade.label"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="gradePosition" />
                  </div>
                </TH>
              {{/if}}
              <th class="print-only-table-cell w-24 text-center">
                {{t "reporting.charts.stadiumEvents.categories.spelling" htmlSafe=true}}
              </th>
              <th class="print-only-table-cell w-24 text-center">
                {{t "reporting.charts.stadiumEvents.categories.grammar" htmlSafe=true}}
              </th>
              <th class="print-only-table-cell w-24 text-center">
                {{t "reporting.charts.stadiumEvents.categories.vocabulary" htmlSafe=true}}
              </th>
              <th class="print-only-table-cell w-24 text-center">
                {{t "reporting.charts.stadiumEvents.categories.usage" htmlSafe=true}}
              </th>
              <TH
                class="w-24"
                role="button"
                {{on "click" (fn this.processor.tableActions.sortByNumber "eventsCompleted")}}
              >
                <div class="flex items-center justify-between">
                  {{t "reporting.charts.stadiumEvents.eventsCompleted" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="eventsCompleted" />
                </div>
              </TH>
              <TH class="w-24" role="button" {{on "click" (fn this.processor.tableActions.sortByNumber "accuracy")}}>
                <div class="flex items-center justify-between">
                  {{t "reporting.charts.stadiumEvents.accuracy" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="accuracy" />
                  <Tooltip @text="*Across all games" role="presentation" />
                </div>
              </TH>
              {{#if this.showChart}}
                <TH
                  class="{{if @subco 'w-1/3' 'w-1/2'}}
                    cursor-default border-none bg-white px-0! whitespace-nowrap text-black print:hidden"
                >
                  <Legend
                    @title={{t "reporting.chartTitles.stadiumEventsHistory"}}
                    @categories={{this.legendCategories}}
                  />
                </TH>
              {{/if}}
            </tr>
          </thead>
          <tbody class="divide-dusty-black-100 divide-y-2 print:divide-black">
            {{#each this.rowsWithChartOptions as |row|}}
              {{! template-lint-disable no-invalid-interactive }}
              <tr class="cursor-pointer" {{on "click" (fn (optional this.transitionToStudentRoute) row.id)}}>
                <TD class="text-oceany-blue-300">
                  {{row.firstName}}
                </TD>
                <TD class="text-oceany-blue-300">
                  {{row.lastName}}
                </TD>
                {{#if @subco}}
                  <TD>
                    <div class="{{if this.showChart 'max-h-20 overflow-auto'}}">
                      {{row.teacherName}}
                    </div>
                  </TD>
                  <TD class="text-center">
                    {{gradeName row.gradePosition}}
                  </TD>
                {{/if}}
                <td class="print-only-table-cell text-center">
                  {{formatValue row.spelling}}
                </td>
                <td class="print-only-table-cell text-center">
                  {{formatValue row.grammar}}
                </td>
                <td class="print-only-table-cell text-center">
                  {{formatValue row.vocabulary}}
                </td>
                <td class="print-only-table-cell text-center">
                  {{formatValue row.usage}}
                </td>
                <TD class="text-center">
                  {{formatValue row.eventsCompleted}}
                </TD>
                <TD class="text-center">
                  {{formatValue row.accuracy append="%"}}
                </TD>
                {{#if this.showChart}}
                  <TD class="border-dusty-black-100 cursor-default border-l px-0! py-0! print:hidden">
                    <BaseHighcharterComponent
                      data-test-stadium-events-highchart-row
                      class="h-full"
                      @options={{row.chartOptions}}
                    />
                  </TD>
                {{/if}}
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
    </div>
  </template>
}

export default StadiumEventsHistory
